// FAFI Touch T3 (FAFI-Touch-Plan §9 / T-TCH-020) — touch preferences, in `fafi.settings.touch` beside
// the bindings blob (§14.2's home). Same discipline as `bindings.ts`: sanitize on load (unknown keys
// dropped, wrong types dropped, numbers clamped), never throw — a corrupt value cannot stop the boot.

export interface TouchSettings {
  /** Idle opacity of the control overlay (the user slider, plan §3). */
  readonly opacity: number;
  readonly handed: 'right' | 'left';
  /** D159: DIVE/PUNCH buttons — opt-in ("too many buttons", first on-glass playtest). */
  readonly darkArts: boolean;
}

export const DEFAULT_TOUCH_SETTINGS: TouchSettings = { opacity: 0.3, handed: 'right', darkArts: false };

export const TOUCH_OPACITY_MIN = 0.15;
export const TOUCH_OPACITY_MAX = 0.6;

const SETTINGS_KEY = 'fafi.settings';

function readSettings(storage: Pick<Storage, 'getItem'>): Record<string, unknown> {
  try {
    const raw = storage.getItem(SETTINGS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

const clampOpacity = (v: number): number => Math.min(TOUCH_OPACITY_MAX, Math.max(TOUCH_OPACITY_MIN, v));

function sanitize(blob: unknown): TouchSettings {
  if (blob === null || typeof blob !== 'object') return { ...DEFAULT_TOUCH_SETTINGS };
  const rec = blob as Record<string, unknown>;
  const op = rec['opacity'];
  return {
    opacity: typeof op === 'number' && Number.isFinite(op) ? clampOpacity(op) : DEFAULT_TOUCH_SETTINGS.opacity,
    handed: rec['handed'] === 'left' ? 'left' : 'right',
    darkArts: typeof rec['darkArts'] === 'boolean' ? rec['darkArts'] : DEFAULT_TOUCH_SETTINGS.darkArts,
  };
}

export function loadTouchSettings(storage: Pick<Storage, 'getItem'>): TouchSettings {
  return sanitize(readSettings(storage)['touch']);
}

export function saveTouchSettings(storage: Pick<Storage, 'getItem' | 'setItem'>, s: TouchSettings): void {
  const settings = readSettings(storage);
  settings['touch'] = sanitize(s);
  storage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}
